import { useState } from "react";
import type {
  BloodType,
  ContractType,
  HrCertificate,
  HrContract,
  HrPersonal,
  UpdateContract,
  UpdatePersonal,
} from "@dentacore/shared-types";
import {
  BLOOD_TYPE_LABELS,
  BLOOD_TYPES,
  MAX_RENEWAL_BYTES,
  RENEWAL_MIME_TYPES,
} from "@dentacore/shared-types";

import { usePhoneCountry } from "../api/use-phone-country";
import { PhoneInput } from "../ui/phone-input";

import { Modal } from "../ui/modal";
import { describeNumberError, NumberField } from "../ui/number-field";
import type { NumberFieldRules } from "../ui/number-field";
import styles from "./staff.module.css";
import { CONTRACT_LABELS, shortDate } from "./team";
import own from "./team.module.css";
import { RequiredMark } from "../ui/required-mark";

/** The notice the column allows (its CHECK, 0–52). */
const NOTICE: NumberFieldRules = { min: 0, max: 52, integer: true, unit: "weeks" };

/**
 * *Change contract* (STORY-181c). Type, probation end and notice — the three
 * things HR owns. Hours stay the rota's and the start and last day stay where
 * they are set (HR-D15). **Only what changed is sent**, so a field nobody
 * touched cannot be overwritten by a stale copy.
 */
export function ChangeContractDialog({
  name,
  contract,
  busy,
  onSave,
  onClose,
}: {
  name: string;
  contract: HrContract;
  busy: boolean;
  onSave: (change: UpdateContract) => void;
  onClose: () => void;
}): React.JSX.Element {
  const [type, setType] = useState<ContractType | "">(contract.contractType ?? "");
  const [probation, setProbation] = useState(contract.probationEndsOn ?? "");
  const [notice, setNotice] = useState(
    contract.noticeWeeks === null ? "" : String(contract.noticeWeeks),
  );
  const noticeError = notice.trim() === "" ? null : describeNumberError(notice, NOTICE);
  const beforeStart =
    probation !== "" && contract.startedOn !== null && probation < contract.startedOn;

  function save(): void {
    const change: UpdateContract = {};
    const nextType = type === "" ? null : type;
    if (nextType !== contract.contractType) change.contractType = nextType;
    const nextProbation = probation === "" ? null : probation;
    if (nextProbation !== contract.probationEndsOn) change.probationEndsOn = nextProbation;
    const nextNotice = notice.trim() === "" ? null : Number(notice);
    if (nextNotice !== contract.noticeWeeks) change.noticeWeeks = nextNotice;
    onSave(change);
  }

  return (
    <Modal
      label={`Change ${name}'s contract`}
      onClose={onClose}
      panelClassName={styles.dialog}
      testId="contract-dialog"
    >
      <div className={styles.dialogHead}>
        <div className={styles.dialogTitles}>
          <h2 className={styles.dialogTitle}>Change contract</h2>
          <p className={styles.dialogSub}>{name}</p>
        </div>
        <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
          &times;
        </button>
      </div>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          Contract
          <RequiredMark />
        </span>
        <select
          className={styles.input}
          value={type}
          disabled={busy}
          onChange={(event) => setType(event.target.value as ContractType | "")}
          data-testid="contract-type"
        >
          <option value="">Not set</option>
          <option value="FULL_TIME">{CONTRACT_LABELS.FULL_TIME}</option>
          <option value="PART_TIME">{CONTRACT_LABELS.PART_TIME}</option>
        </select>
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Probation ends</span>
        <input
          type="date"
          className={styles.input}
          value={probation}
          min={contract.startedOn ?? undefined}
          disabled={busy}
          onChange={(event) => setProbation(event.target.value)}
          data-testid="contract-probation"
        />
        {beforeStart && contract.startedOn !== null ? (
          <p className={own.fieldError} role="alert">
            Before they started, on {shortDate(contract.startedOn)}.
          </p>
        ) : (
          <p className={own.fieldNote}>Leave it empty once the probation is passed.</p>
        )}
      </label>

      <NumberField
        label="Notice, weeks"
        className={styles.field}
        labelClassName={styles.fieldLabel}
        inputClassName={styles.input}
        value={notice}
        onChange={setNotice}
        rules={NOTICE}
        disabled={busy}
        testId="contract-notice"
      />

      <div className={styles.dialogFoot}>
        <button type="button" className={styles.ghost} onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.primary}
          disabled={busy || noticeError !== null || beforeStart}
          onClick={save}
          data-testid="contract-save"
        >
          Save the contract
        </button>
      </div>
    </Modal>
  );
}

/**
 * *Renew* a certificate (HR-D4, HR-D10). From Team it is a manager renewing, so
 * it **counts at once**. A certificate that runs out needs a later date; the
 * document is optional, a PDF, JPEG or PNG of 10 MB at most — checked here so
 * nobody waits on an upload the server will refuse.
 */
export function RenewDialog({
  name,
  certificate,
  countsAtOnce,
  busy,
  onSave,
  onClose,
}: {
  /** Whose it is. */
  name: string;
  certificate: HrCertificate;
  /** True for a manager; the person's own renewal waits for a check. */
  countsAtOnce: boolean;
  busy: boolean;
  onSave: (expiresOn: string | undefined, file: File | null) => void;
  onClose: () => void;
}): React.JSX.Element {
  const [expiresOn, setExpiresOn] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const inForce = certificate.expiresOn;
  const dateProblem =
    inForce === null
      ? null
      : expiresOn === ""
        ? "A renewal needs its new expiry date."
        : expiresOn <= inForce
          ? `Later than ${shortDate(inForce)}, the date in force. To correct a date, edit the certificate.`
          : null;
  const fileProblem =
    file === null
      ? null
      : !(RENEWAL_MIME_TYPES as readonly string[]).includes(file.type)
        ? "Only a PDF, JPEG or PNG."
        : file.size > MAX_RENEWAL_BYTES
          ? "A document is capped at 10 MB."
          : null;
  const nothing = inForce === null && expiresOn === "" && file === null;

  return (
    <Modal
      label={`Renew ${certificate.name}`}
      onClose={onClose}
      panelClassName={styles.dialog}
      testId="renew-dialog"
    >
      <div className={styles.dialogHead}>
        <div className={styles.dialogTitles}>
          <h2 className={styles.dialogTitle}>Renew {certificate.name}</h2>
          <p className={styles.dialogSub}>
            {name}
            {inForce === null ? " · does not run out" : ` · runs out ${shortDate(inForce)}`}
          </p>
        </div>
        <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
          &times;
        </button>
      </div>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          New expiry
          <RequiredMark />
        </span>
        <input
          type="date"
          className={styles.input}
          value={expiresOn}
          min={inForce ?? undefined}
          disabled={busy}
          onChange={(event) => setExpiresOn(event.target.value)}
          data-testid="renew-expiry"
        />
        {expiresOn !== "" && dateProblem !== null && (
          <p className={own.fieldError} role="alert">
            {dateProblem}
          </p>
        )}
      </label>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Document</span>
        <input
          type="file"
          accept={RENEWAL_MIME_TYPES.join(",")}
          disabled={busy}
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          data-testid="renew-file"
        />
        {fileProblem === null ? (
          <p className={own.fieldNote}>A PDF, JPEG or PNG, 10 MB at most.</p>
        ) : (
          <p className={own.fieldError} role="alert">
            {fileProblem}
          </p>
        )}
      </label>

      <p className={own.fieldNote}>
        {countsAtOnce
          ? "It counts at once — the new date is in force as soon as you save."
          : "It waits for a manager to check it. Until then the old date stands."}
      </p>

      <div className={styles.dialogFoot}>
        <button type="button" className={styles.ghost} onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.primary}
          disabled={busy || dateProblem !== null || fileProblem !== null || nothing}
          onClick={() => onSave(expiresOn === "" ? undefined : expiresOn, file)}
          data-testid="renew-save"
        >
          {countsAtOnce ? "Renew" : "Send for a check"}
        </button>
      </div>
    </Modal>
  );
}

/** The blood type select's value while the reader may not see the one recorded (SP-D3). */
const UNCHANGED = "unchanged";

/**
 * *Change personal details* (STORY-192, SP-D1). Date of birth, blood type and
 * one emergency contact. **Only what changed is sent**, as with the contract.
 * A reader who may not see the blood type (SP-D3) can still record one, but
 * the field starts on *Leave as it is* rather than pretending none is recorded.
 */
export function ChangePersonalDialog({
  name,
  personal,
  today,
  busy,
  onSave,
  onClose,
}: {
  name: string;
  personal: HrPersonal;
  today: string;
  busy: boolean;
  onSave: (change: UpdatePersonal) => void;
  onClose: () => void;
}): React.JSX.Element {
  const country = usePhoneCountry();
  const contact = personal.emergencyContact;
  const [born, setBorn] = useState(personal.dateOfBirth ?? "");
  const [blood, setBlood] = useState<BloodType | "" | typeof UNCHANGED>(
    personal.bloodTypeHidden ? UNCHANGED : (personal.bloodType ?? ""),
  );
  const [contactName, setContactName] = useState(contact?.name ?? "");
  const [relation, setRelation] = useState(contact?.relation ?? "");
  const [phone, setPhone] = useState(contact?.phoneE164 ?? "");

  const future = born !== "" && born > today;
  // A relation or a number with nobody to call is not a contact.
  const nameless = contactName.trim() === "" && (relation.trim() !== "" || phone !== "");
  const phoneIncomplete = phone !== "" && !phone.startsWith("+");

  function save(): void {
    const change: UpdatePersonal = {};
    const nextBorn = born === "" ? null : born;
    if (nextBorn !== personal.dateOfBirth) change.dateOfBirth = nextBorn;
    if (blood !== UNCHANGED) {
      const nextBlood = blood === "" ? null : blood;
      if (personal.bloodTypeHidden || nextBlood !== personal.bloodType)
        change.bloodType = nextBlood;
    }
    const nextContact =
      contactName.trim() === ""
        ? null
        : {
            name: contactName.trim(),
            relation: relation.trim() === "" ? null : relation.trim(),
            phoneE164: phone === "" ? null : phone,
          };
    if (JSON.stringify(nextContact) !== JSON.stringify(contact))
      change.emergencyContact = nextContact;
    onSave(change);
  }

  return (
    <Modal
      label={`Change ${name}'s personal details`}
      onClose={onClose}
      panelClassName={styles.dialog}
      testId="personal-dialog"
    >
      <div className={styles.dialogHead}>
        <div className={styles.dialogTitles}>
          <h2 className={styles.dialogTitle}>Change personal details</h2>
          <p className={styles.dialogSub}>{name}</p>
        </div>
        <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
          &times;
        </button>
      </div>

      <div className={styles.pair}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Date of birth</span>
          <input
            type="date"
            className={styles.input}
            value={born}
            max={today}
            disabled={busy}
            onChange={(event) => setBorn(event.target.value)}
            data-testid="personal-born"
          />
          {future && (
            <p className={own.fieldError} role="alert">
              That is in the future.
            </p>
          )}
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Blood type</span>
          <select
            className={styles.input}
            value={blood}
            disabled={busy}
            onChange={(event) => setBlood(event.target.value as BloodType | "")}
            data-testid="personal-blood"
          >
            {personal.bloodTypeHidden && <option value={UNCHANGED}>Leave as it is</option>}
            <option value="">Not recorded</option>
            {BLOOD_TYPES.map((type) => (
              <option key={type} value={type}>
                {BLOOD_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className={own.fieldNote}>
        Health data: only an administrator and the person themselves see the blood type.
      </p>

      <h3 className={own.dialogSection}>Emergency contact</h3>
      <div className={styles.pair}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Name</span>
          <input
            className={styles.input}
            value={contactName}
            maxLength={120}
            disabled={busy}
            onChange={(event) => setContactName(event.target.value)}
            data-testid="personal-contact-name"
          />
          {nameless && (
            <p className={own.fieldError} role="alert">
              Who is it? A contact needs a name.
            </p>
          )}
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Relation</span>
          <input
            className={styles.input}
            value={relation}
            maxLength={60}
            placeholder="Sister, partner, neighbour…"
            disabled={busy}
            onChange={(event) => setRelation(event.target.value)}
            data-testid="personal-contact-relation"
          />
        </label>
      </div>
      <PhoneInput
        value={phone}
        onChange={setPhone}
        defaultCountry={country}
        testIdPrefix="personal-contact-phone"
        invalid={phoneIncomplete}
      />

      <div className={styles.dialogFoot}>
        <button type="button" className={styles.ghost} onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.primary}
          disabled={busy || future || nameless || phoneIncomplete}
          onClick={save}
          data-testid="personal-save"
        >
          Save personal details
        </button>
      </div>
    </Modal>
  );
}
